import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useBlocker } from "react-router-dom";
import { authenticatedIdentity } from "../../../_services/apiFetch";
import {
    FiUpload, FiTrash2, FiCheck, FiX,
    FiUsers, FiTruck, FiFileText, FiFile,
    FiBookOpen, FiUser, FiMapPin, FiMail,
    FiPhone, FiHash, FiShield, FiEdit3, FiHome, FiDollarSign, FiCheckCircle,
    FiChevronDown, FiShoppingCart,
} from "react-icons/fi";

/* Design Tokens */
export const T = {
    radius: "12px",
    radiusSm: "8px",
    border: "#cbd5e1",
    bg: "#f7f9fc",
    cardBg: "#ffffff",
    textPrimary: "#0f172a",
    textMuted: "#64748b",
    textLight: "#94a3b8",
    accent: "#2563eb",
    success: "#059669",
    danger: "#dc2626",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
    fontSizeBase: "16px",
    fontSizeLg: "17px",
    fontSizeSm: "15px",
    fontSizeXs: "14px",
};

/* StatusBadge */
export function StatusBadge({ value = "Active", onClick }) {
    const isActive = String(value).toLowerCase() === "active";
    const statusStyles = {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: isActive ? "#ecfdf5" : "#fef2f2",
        color: isActive ? T.success : T.danger,
        border: `1px solid ${isActive ? "#a7f3d0" : "#fecaca"}`,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: T.fontSizeXs,
        fontWeight: 600,
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        transition: "all 0.2s ease",
    };

    return (
        <span onClick={onClick} style={statusStyles}>
            <span style={{
                width: 6, height: 6, borderRadius: "50%",
                background: isActive ? T.success : T.danger,
            }} />
            {isActive ? "Active" : "Inactive"}
        </span>
    );
}

/* Avatar */
export function Avatar({ logoUrl, name, size = 60 }) {
    const initial = (name?.trim()?.[0] ?? "C").toUpperCase();
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%",
            border: `2px solid ${T.border}`, background: "#eef2f7",
            overflow: "hidden", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 0 4px #f0f4ff",
        }}>
            {logoUrl
                ? <img src={logoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ fontSize: size * 0.38, fontWeight: 700, color: "#4f6a8a" }}>{initial}</span>
            }
        </div>
    );
}

/* StatCard */
export function StatCard({ icon: Icon, color, label, value, hint }) {
    const chipBg = color.replace(")", ",0.10)").replace("rgb", "rgba");
    return (
        <div title={hint} style={{
            background: T.cardBg, border: `1px solid ${T.border}`,
            borderRadius: T.radius, padding: "14px 18px",
            boxShadow: T.shadow, display: "flex", alignItems: "center", gap: 14,
        }}>
            <div style={{
                width: 40, height: 40, borderRadius: "10px",
                background: chipBg, color, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon size={18} />
            </div>
            <div>
                <div style={{ fontSize: T.fontSizeXs, color: T.textLight, marginBottom: 1 }}>{label}</div>
                <div style={{ fontSize: T.fontSizeLg, fontWeight: 700, color: T.textPrimary, lineHeight: 1 }}>{value ?? "—"}</div>
            </div>
        </div>
    );
}

/* SectionHeading */
export function SectionHeading({ icon: Icon, title }) {
    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 8,
            paddingBottom: 10, marginBottom: 14,
            borderBottom: `1px solid ${T.border}`,
        }}>
            <div style={{
                width: 24, height: 24, borderRadius: "6px",
                background: "#eff6ff", color: T.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon size={12} />
            </div>
            <span style={{ fontSize: T.fontSizeSm, fontWeight: 700, color: T.textPrimary }}>{title}</span>
        </div>
    );
}

/* InfoRow (view mode) */
export function InfoRow({ icon: Icon, label, children }) {
    return (
        <div style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            padding: "10px 0", borderBottom: `1px solid ${T.bg}`,
        }}>
            <div style={{
                width: 28, height: 28, borderRadius: "7px",
                background: T.bg, color: T.textMuted, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icon size={13} />
            </div>
            <div>
                <div style={{ fontSize: T.fontSizeXs, color: T.textLight, fontWeight: 500, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: T.fontSizeBase, color: T.textPrimary, fontWeight: 500 }}>{children || "—"}</div>
            </div>
        </div>
    );
}

/* FieldInput (edit mode)
   FIX: Status <select> now has an animated FiChevronDown arrow like Customer page */
export function FieldInput({ label, name, value, onChange, type = "text", as, rows, children, placeholder, required, error }) {
    const [focused, setFocused] = useState(false);
    const base = {
        width: "100%", fontSize: T.fontSizeBase, color: T.textPrimary,
        background: "#fff", fontFamily: "inherit",
        // Red border and the message under it, as on the Sales and Purchasing forms.
        border: `1.5px solid ${error ? T.danger : focused ? T.accent : T.border}`,
        borderRadius: T.radiusSm, outline: "none",
        boxShadow: focused && !error ? "0 0 0 3px rgba(37,99,235,0.10)" : "none",
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxSizing: "border-box",
    };
    const shared = {
        name, value, onChange, placeholder,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
    };
    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: T.fontSizeXs, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                {label} {required && <span style={{ color: T.danger }}>*</span>}
            </label>
            {as === "textarea" ? (
                <textarea rows={rows ?? 3} {...shared} style={{ ...base, padding: "9px 12px", resize: "vertical", display: "block" }} />
            ) : as === "select" ? (
                /* FIX: Animated chevron arrow, same as Customer page SearchableSelect */
                <div style={{ position: "relative" }}>
                    <select
                        {...shared}
                        style={{
                            ...base,
                            height: 36,
                            padding: "0 30px 0 11px",
                            display: "block",
                            cursor: "pointer",
                            appearance: "none",
                            WebkitAppearance: "none",
                        }}
                    >
                        {children}
                    </select>
                    <span style={{
                        position: "absolute", right: 9, top: "50%",
                        transform: `translateY(-50%) rotate(${focused ? 180 : 0}deg)`,
                        pointerEvents: "none",
                        transition: "transform 0.2s ease",
                        display: "inline-flex", alignItems: "center",
                        color: T.textLight,
                    }}>
                        <FiChevronDown size={14} />
                    </span>
                </div>
            ) : (
                <input type={type} {...shared} style={{ ...base, height: 36, padding: "0 11px", display: "block" }} />
            )}
            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}
        </div>
    );
}

/* Banner */
export function Banner({ type, msg, onClose }) {
    if (!msg) return null;
    const colors = {
        success: { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0" },
        danger: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca" },
        info: { bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe" },
    };
    const c = colors[type] ?? colors.info;
    return (
        <div style={{
            padding: "11px 16px", borderRadius: T.radiusSm, marginBottom: 16,
            fontSize: T.fontSizeSm, fontWeight: 500, background: c.bg, color: c.color,
            border: `1px solid ${c.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
            <span>{msg}</span>
            <button onClick={onClose} style={{
                background: "none", border: "none", cursor: "pointer",
                color: "inherit", padding: 0, lineHeight: 1, flexShrink: 0,
            }}>
                <FiX size={13} />
            </button>
        </div>
    );
}

/* Actions Panel (edit mode sidebar) */
export function ActionsPanel({ form, onChange, onUpload, onRemoveLogo, onSave, onCancel, canSave, saving, saveLabel }) {
    return (
        <div style={{
            flexShrink: 0, width: 190,
            borderLeft: `1px solid ${T.border}`,
            padding: "20px 16px",
            background: T.bg,
            display: "flex", flexDirection: "column", gap: 12,
        }}>
            <div style={{ fontSize: T.fontSizeSm, fontWeight: 700, color: T.textPrimary }}>Actions</div>

            {/* Upload logo */}
            <div>
                <button onClick={onUpload} style={{
                    width: "100%", padding: "8px 10px",
                    background: "#fff", border: `1.5px solid ${T.border}`,
                    borderRadius: T.radiusSm, cursor: "pointer",
                    fontSize: 12, fontWeight: 500, color: T.textPrimary,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    boxSizing: "border-box",
                }}>
                    <FiUpload size={12} />
                    {form.LogoUrl ? "Replace Logo" : "Upload Logo"}
                </button>

                {form.LogoUrl && (
                    <button onClick={onRemoveLogo} style={{
                        width: "100%", padding: "7px 10px", marginTop: 6,
                        background: "#fff", border: `1.5px solid #fecaca`,
                        borderRadius: T.radiusSm, cursor: "pointer",
                        fontSize: 12, fontWeight: 500, color: T.danger,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        boxSizing: "border-box",
                    }}>
                        <FiTrash2 size={12} /> Remove Logo
                    </button>
                )}
                <p style={{ fontSize: T.fontSizeXs, color: T.textLight, margin: "6px 0 0", lineHeight: 1.5 }}>
                    Supports SVG, PNG, JPG, WEBP, AVIF. Max size: 5MB.
                </p>
            </div>

            <div style={{ borderTop: `1px solid ${T.border}` }} />

            {/* Save */}
            <button onClick={onSave} disabled={!canSave || saving} style={{
                width: "100%", padding: "9px",
                background: canSave && !saving ? T.accent : "#93c5fd",
                color: "#fff", border: "none",
                borderRadius: T.radiusSm,
                cursor: canSave && !saving ? "pointer" : "not-allowed",
                fontSize: T.fontSizeSm, fontWeight: 600,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                boxShadow: canSave && !saving ? "0 2px 8px rgba(37,99,235,0.25)" : "none",
                boxSizing: "border-box",
            }}>
                {saving
                    ? <><Spinner size="sm" />&nbsp;Saving…</>
                    : <><FiCheck size={13} /> {saveLabel || "Save Changes"}</>
                }
            </button>

            {/* Cancel */}
            <button onClick={onCancel} disabled={saving} style={{
                width: "100%", padding: "8px",
                background: "#fff", border: `1.5px solid ${T.border}`,
                borderRadius: T.radiusSm, cursor: "pointer",
                fontSize: T.fontSizeSm, fontWeight: 500, color: T.textMuted,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                boxSizing: "border-box",
            }}>
                <FiX size={13} /> Cancel
            </button>
        </div>
    );
}

/* Unsaved changes guard (edit mode)
   Asks before leaving the page — a menu link, Back, closing or reloading the
   tab — while the profile has unsaved changes. Like the Machining guard, it
   steps aside when the login changes (sign out / switch account). */
export function UnsavedChangesGuard({ blocked }) {
    const identity = useRef(authenticatedIdentity());
    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        blocked
        && identity.current === authenticatedIdentity()
        && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        const prevent = (event) => {
            if (!blocked || identity.current !== authenticatedIdentity()) return;
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", prevent);
        return () => window.removeEventListener("beforeunload", prevent);
    }, [blocked]);

    return (
        <Modal show={blocker.state === "blocked"} onHide={() => blocker.reset?.()} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: T.fontSizeLg }}>Unsaved changes</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: T.fontSizeBase }}>
                Your company profile changes haven't been saved. Leave this page and discard them?
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => blocker.reset?.()}>
                    Keep editing
                </Button>
                <Button variant="danger" onClick={() => blocker.proceed?.()}>
                    Discard and leave
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export const STAT_DEFS = [
    { icon: FiUsers, color: "rgb(37,99,235)", label: "Customers", key: "customers" },
    { icon: FiTruck, color: "rgb(5,150,105)", label: "Suppliers", key: "suppliers" },
    { icon: FiFileText, color: "rgb(217,119,6)", label: "Invoices", key: "invoices" },
    { icon: FiShoppingCart, color: "rgb(220,53,69)", label: "Purchase Orders", key: "purchaseOrders" },
];

export { FiBookOpen, FiUser, FiMapPin, FiMail, FiPhone, FiHash, FiShield, FiEdit3, FiCheck, FiHome, FiDollarSign, FiCheckCircle };